import * as THREE from 'three';
import { mergeGeometries, mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { avatarDetail } from '../lod.js';
import { HEAD_PROFILE, PERSON_LANDMARKS } from './anatomy.js';
import { profile, sampleProfile } from './profile.js';
import { strandFlow, longStrandFlow, curlRelief } from './hairDesign.js';

const TAU = Math.PI * 2;
const smooth = (x, a, b) => THREE.MathUtils.smoothstep(x, a, b);
const headY = PERSON_LANDMARKS.headY;
const LONG_PROFILE = [
  { y: -1.20, rx: .24, rz: .115, z: -.29 },
  { y: -1.11, rx: .28, rz: .128, z: -.29 },
  { y: -1.02, rx: .30, rz: .14, z: -.28 },
  { y: -.90, rx: .322, rz: .15, z: -.28 },
  { y: -.78, rx: .33, rz: .16, z: -.27 },
  { y: -.52, rx: .35, rz: .19, z: -.23 },
  { y: -.30, rx: .36, rz: .24, z: -.12 },
  { y: -.18, rx: .375, rz: .30, z: -.04 },
  ...HEAD_PROFILE.filter(r => r.y > -.18)
];

// One continuous shell per style. The same skull sections define the attachment
// in both detail modes; additional samples describe curls, not hidden spheres.
function shell(style) {
  const studio = avatarDetail() === 'studio';
  const segments = style === 'curly' ? (studio ? 64 : 48) : ['cropped','pompadour','long'].includes(style) ? (studio ? 48 : 40) : (studio ? 40 : 32);
  const sections = style === 'long' ? LONG_PROFILE : HEAD_PROFILE;
  let levels = sections.map(r => r.y + .015).reverse();
  if (['cropped','pompadour'].includes(style)) levels = [.42,.38,.32,.245,.16,.075,-.025,-.17];
  if (style === 'pompadour') levels = [.42,.39,.355,.33,.30,.27,.245,.19,.13,.075,-.025,-.17];
  if (style === 'curly') levels = levels.flatMap((y, i) => i < levels.length - 1
    ? [y, THREE.MathUtils.lerp(y, levels[i + 1], 1 / 3), THREE.MathUtils.lerp(y, levels[i + 1], 2 / 3)] : [y]);
  const positions = [], indices = [];
  const point = (a, level) => {
    const s = Math.sin(a), c = Math.cos(a), front = Math.max(0, c);
    let edge = -.19 + .37 * front ** 2;
    if (['cropped','pompadour','spiky-base'].includes(style)) {
      // Expose the ears and taper the nape. Two short temple points connect the
      // side cut to an asymmetric fringe instead of one continuous helmet rim.
      const temple = Math.exp(-Math.pow((Math.abs(Math.atan2(s,c))-1.10)/.17,2));
      edge = -.12 + .30*smooth(c,-.05,.70) + .18*Math.abs(s)**10 - .045*temple;
      const fringe=style==='cropped'?.045:style==='pompadour'?.005:.033;
      edge += smooth(c,.45,.85)*(-fringe*(.5+.5*Math.cos(11*a+.5))-.012*s);
    }
    if (style === 'long') {
      // Center the opening and keep both full eyebrows clear. The broader
      // arch meets the face-framing locks outside the brows; strand relief
      // still varies across the crown and fall to avoid a uniform helmet.
      const face = front ** 1.8;
      edge = -.22 + .47 * face;
      edge += .008 * Math.cos(7*a) * face;
      edge -= (1 + .065 * Math.cos(10*a+.7)) * (1-smooth(c,-.18,.30));
    }
    if (style === 'curly') {
      edge=-.14+.31*smooth(c,-.1,.7)+.08*Math.abs(s)**8;
      edge+=.023*Math.sin(9*a+.7);
    }
    const y = Math.max(level, edge), r = sampleProfile(sections, y - .015);
    const crown = smooth(y, .08, .42);
    let width = .019, lift = 0, shiftX = 0, shiftZ = 0;
    if (style === 'cropped') {
      const x=r.rx*Math.sign(s)*Math.abs(s)**.8,z=r.rz*Math.sign(c)*Math.abs(c)**.8+(r.z||0);
      const top=smooth(y,.06,.32),flow=strandFlow('short',x,y,z);
      // Broad swept ridges are part of the outer surface, not separate spikes.
      // Close-cut sides stay close to the scalp while the top carries the volume.
      width=.011+.013*top+.012*front*smooth(y,edge,edge+.12);
      width+=.035*front*flow*smooth(y,.11,.22)*(1-smooth(y,.32,.42));
      lift=top*(.02+.025*flow+.025*front);
      lift-=.018*smooth(y,.38,.42);
      shiftX=-.025*top;
    } else if (style === 'spiky-base') {
      // A fuller combed layer supports the spikes while tapering into the scalp
      // at the hairline. Low ridges connect the visible spaces between locks.
      const body = smooth(y,-.05,.30), root = smooth(y,edge,edge+.10);
      const flow = strandFlow('short',r.rx*s,y,r.rz*c);
      width = .014 + root*body*(.035+.018*flow);
      lift = root*crown*(.065+.02*flow*(1-smooth(y,.34,.42)));
      shiftX = -.022 * crown;
    } else if (style === 'pompadour') {
      // A rounded front roll feeds into swept locks on the crown. Concentrate
      // height in the center so the temples do not rise into a rectangular wall.
      const x=r.rx*Math.sign(s)*Math.abs(s)**.8,z=r.rz*c+(r.z||0);
      const sweep = THREE.MathUtils.clamp((z + .35) / .7, 0, 1);
      const flow=strandFlow('pompadour',x,y,z),top=smooth(y,.08,.35);
      const root=smooth(y,edge,edge+.11),center=1-smooth(Math.abs(x+.025),.16,.36);
      const crest=top*root*center,relief=1-smooth(y,.38,.42);
      const part=Math.exp(-Math.pow((x-.20-.12*z)/.036,2))*top;
      lift=top*root*.025+crest*(.19*sweep**1.3+.045*flow*relief);
      lift-=.027*part*root;
      width=.009+.014*top*root;
      width+=front*root*center*(.085*Math.sin(Math.PI*smooth(y,.15,.42))+.045*flow*relief);
      width-=.006*part*root;
      shiftX=-.04*crest*sweep;
      shiftZ=-.065*top*root*sweep;
      shiftZ-=.13*front*root*smooth(y,.245,.355)*(1-smooth(y,.355,.42));
    } else if (style === 'curly') {
      // Rounded clusters displace a shared surface. There are no individual
      // curl solids or intersections, and relief follows the crown as well.
      const direction = new THREE.Vector3(s * r.rx, y, c * r.rz).normalize();
      const relief = .03 + curlRelief(direction.x,direction.y,direction.z);
      width += relief * Math.sqrt(Math.max(0, 1 - direction.y ** 2));
      lift = relief * direction.y;
    } else if (style === 'long') {
      const flow = longStrandFlow(a,y), root = smooth(y,edge,edge+.12);
      const x = r.rx*Math.sign(s)*Math.abs(s)**.8;
      const part = Math.exp(-Math.pow(x/.035,2))*front;
      // Close to the scalp at the hairline; broad ridges grow behind it.
      // A recessed part separates the two swept masses instead of a smooth dome.
      const relief = 1-smooth(y,.32,.42);
      width = .006 + (.014+.055*flow*relief) * root;
      width += .018 * Math.abs(s)**8 * smooth(y,-.3,0) * (1-crown);
      lift = crown * (.018+(.045*flow+.025*Math.abs(s)*front)*relief) * root;
      lift -= .025 * part * crown * root;
      width -= .012 * part * crown * root;
      const fall=1-smooth(y,-.5,.05);
      shiftX=.025*Math.sin(5*y+.6)*fall;
      shiftZ=.025*Math.sin(4*y+.8)*fall;
    }
    return [
      (r.rx + width) * Math.sign(s) * Math.abs(s) ** .8 + shiftX,
      y + headY + lift,
      (r.rz + width) * Math.sign(c) * Math.abs(c) ** .8 + (r.z || 0) + shiftZ
    ];
  };
  for (let row = 0; row < levels.length; row++) for (let i = 0; i < segments; i++) {
    positions.push(...point(i * TAU / segments, levels[row]));
    if (row < levels.length - 1) {
      const k = row * segments + i, next = row * segments + (i + 1) % segments;
      indices.push(k, k + segments, next, next, k + segments, next + segments);
    }
  }
  const center = positions.length / 3;
  const top = new THREE.Vector3();
  for (let i = 0; i < segments; i++) top.add(new THREE.Vector3().fromArray(positions, i * 3));
  top.divideScalar(segments); top.y += .004;
  positions.push(...top.toArray());
  for (let i = 0; i < segments; i++) indices.push(center, i, (i + 1) % segments);
  const raw = new THREE.BufferGeometry();
  raw.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  raw.setIndex(indices);
  const geo = mergeVertices(raw), clean = [];
  for (let i = 0; i < geo.index.count; i += 3) {
    const a = geo.index.getX(i), b = geo.index.getX(i + 1), c = geo.index.getX(i + 2);
    if (a !== b && b !== c && a !== c) clean.push(a, b, c);
  }
  geo.setIndex(clean);
  geo.computeVertexNormals();
  raw.dispose();
  return style === 'long' ? thicken(geo) : sealRoots(geo);
}

// A displaced shell can overhang the skull beside the ears. Close only its
// boundary onto the actual scalp: a narrow rim, without duplicating the shell.
function sealRoots(outer) {
  const edges=new Map(),p=outer.attributes.position;
  for(let i=0;i<outer.index.count;i+=3)for(let j=0;j<3;j++) {
    const a=outer.index.getX(i+j),b=outer.index.getX(i+(j+1)%3),key=`${Math.min(a,b)}:${Math.max(a,b)}`;
    if(edges.has(key))edges.delete(key);else edges.set(key,[a,b]);
  }
  const skull=profile(HEAD_PROFILE,{segments:avatarDetail()==='studio'?24:16,power:.8});
  const material=new THREE.MeshBasicMaterial(),mesh=new THREE.Mesh(skull,material);
  mesh.position.y=headY;mesh.updateMatrixWorld(true);
  const ray=new THREE.Raycaster(),center=new THREE.Vector3(0,headY,0),roots=new Map();
  const root=i=>{
    if(roots.has(i))return roots.get(i);
    const out=new THREE.Vector3().fromBufferAttribute(p,i),direction=out.clone().sub(center).normalize();
    ray.set(center.clone().addScaledVector(direction,2),direction.clone().negate());
    const hit=ray.intersectObject(mesh)[0];
    if(!hit)throw Error('Hairline has no scalp beneath it');
    const point=hit.point.addScaledVector(direction,-.008);roots.set(i,point);return point;
  };
  const positions=[],indices=[];
  for(const [a,b] of edges.values()) {
    const k=positions.length/3;
    positions.push(p.getX(a),p.getY(a),p.getZ(a),p.getX(b),p.getY(b),p.getZ(b),...root(a).toArray(),...root(b).toArray());
    indices.push(k+1,k,k+2,k+1,k+2,k+3);
  }
  const rim=new THREE.BufferGeometry();rim.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));rim.setIndex(indices);rim.computeVertexNormals();
  const result=mergeGeometries([outer,rim]);outer.dispose();rim.dispose();skull.dispose();material.dispose();return result;
}

// The long nape is visible from inside when viewed from the front. Give it a
// real inner wall and a joined hem, without enabling double-sided materials.
function thicken(outer) {
  const inner = outer.clone(), p = inner.attributes.position;
  for (let i = 0; i < p.count; i++) p.setXYZ(i, p.getX(i) * .91, p.getY(i), p.getZ(i) * .91 + .008);
  for (let i = 0; i < inner.index.count; i += 3) {
    const a = inner.index.getX(i);
    inner.index.setX(i, inner.index.getX(i + 1)); inner.index.setX(i + 1, a);
  }
  inner.computeVertexNormals();
  const edges = new Map();
  for (let i = 0; i < outer.index.count; i += 3) {
    const tri = [0, 1, 2].map(j => outer.index.getX(i + j));
    for (let j = 0; j < 3; j++) {
      const a = tri[j], b = tri[(j + 1) % 3], key = `${Math.min(a, b)}:${Math.max(a, b)}`;
      if (edges.has(key)) edges.delete(key); else edges.set(key, [a, b]);
    }
  }
  const positions = [], indices = [], op = outer.attributes.position;
  for (const [a, b] of edges.values()) {
    const k = positions.length / 3;
    for (const [attr, i] of [[op, a], [op, b], [p, a], [p, b]]) positions.push(attr.getX(i), attr.getY(i), attr.getZ(i));
    indices.push(k + 1, k, k + 2, k + 1, k + 2, k + 3);
  }
  const hem = new THREE.BufferGeometry();
  hem.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  hem.setIndex(indices); hem.computeVertexNormals();
  const result = mergeGeometries([outer, inner, hem]);
  outer.dispose(); inner.dispose(); hem.dispose();
  return result;
}

function spiky() {
  const base = shell('spiky-base');
  const material = new THREE.MeshBasicMaterial();
  const surface = new THREE.Mesh(base, material);
  surface.updateMatrixWorld(true);
  const skull = profile(HEAD_PROFILE, { segments: avatarDetail() === 'studio' ? 24 : 16, power: .8 });
  const scalp = new THREE.Mesh(skull, material);
  scalp.position.y = headY; scalp.updateMatrixWorld(true);
  const ray = new THREE.Raycaster(), origin = new THREE.Vector3(0, headY, 0);
  const tufts = [];
  // Alternate longer locks and short infill across the crown, without rows.
  // Point tips and buried open roots avoid tiny caps and hidden faces.
  // Game needs only one intermediate section; studio retains the longer bends.
  const studio = avatarDetail() === 'studio';
  const lockCount = 96, lockSegments = studio ? 5 : 4;
  for (let i = 0; i < lockCount; i++) {
    const a = i * 2.39996323 + .3;
    const polar = Math.max(.04,Math.acos(1 - (i + .5) / lockCount * .70) - .18 * Math.max(0, Math.cos(a)));
    const normal = new THREE.Vector3(Math.sin(polar) * Math.sin(a), Math.cos(polar), Math.sin(polar) * Math.cos(a));
    ray.set(origin.clone().addScaledVector(normal, 2), normal.clone().negate());
    const hit = ray.intersectObject(surface)[0];
    if (!hit) throw new Error('Hair lock has no scalp support');
    const variation = .5 + .5 * Math.sin(i * 2.17);
    const infill = i % 2 === 1;
    const length = infill ? .085+.075*variation : .15+.105*variation;
    const width = (infill ? .037 : .05) + .015 * (.5 + .5 * Math.cos(i * 1.73));
    const direction = normal.clone().multiplyScalar(.65)
      .add(new THREE.Vector3(-.08 + .07 * Math.sin(a), .85, -.24)).normalize();
    const rings = [
      { y: -.04, rx: width, rz: width * .72 },
      ...(!studio || infill ? [{ y: length*.42, rx: width*.55, rz: width*.38, z: -.008 }] : [
        { y: length*.22, rx: width*.78, rz: width*.54 },
        { y: length*.64, rx: width*.38, rz: width*.26, z: -.012 }
      ])
    ];
    const lock = profile(rings, { segments: lockSegments, power: 1, caps: 'none' });
    const vertices = Array.from(lock.attributes.position.array), indices = Array.from(lock.index.array);
    const tip = vertices.length / 3, lastRing = tip - lockSegments;
    vertices.push(-.012, length, -.028);
    for (let j = 0; j < lockSegments; j++) indices.push(lastRing+j,lastRing+(j+1)%lockSegments,tip);
    lock.setAttribute('position',new THREE.Float32BufferAttribute(vertices,3));
    lock.setIndex(indices);
    lock.deleteAttribute('normal');
    const positions = lock.attributes.position, rootVertices = [];
    for (let j = 0; j < positions.count; j++) if (positions.getY(j) < -.039) rootVertices.push(j);
    lock.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction));
    lock.translate(hit.point.x, hit.point.y, hit.point.z);
    // Embed the entire open root footprint in the actual skull.
    // Moving only the lock center left the old wide bases hanging off the sides.
    for (const j of rootVertices) {
      const radial = new THREE.Vector3().fromBufferAttribute(positions, j).sub(origin).normalize();
      ray.set(origin.clone().addScaledVector(radial, 2), radial.clone().negate());
      const root = ray.intersectObject(scalp)[0];
      if (!root) throw new Error('Hair lock root has no scalp support');
      root.point.addScaledVector(radial, -.012);
      positions.setXYZ(j, root.point.x, root.point.y, root.point.z);
    }
    lock.computeVertexNormals();
    tufts.push(lock);
  }
  material.dispose(); skull.dispose();
  const geo = mergeGeometries([base, ...tufts]);
  base.dispose(); tufts.forEach(g => g.dispose());
  return geo;
}

export function buildHair() {
  return {
    short: shell('cropped'), pompadour: shell('pompadour'), spiky: spiky(),
    long: shell('long'), curly: shell('curly')
  };
}
